import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useCreateWallet, usePrivy, useWallets } from '@privy-io/react-auth'
import { useSetActiveWallet } from '@privy-io/wagmi'
import { useAccount, useDisconnect } from 'wagmi'

import { Address } from '~/utils/address'
import { emailsMatch } from '~/utils/email'
import { captureException } from '~/utils/errors'

export type PrivyWalletBridge = {
  authenticated: boolean
  hasPrivyUser: boolean
  privyEmail?: string
  isActivatingWallet: boolean
  isLoggingOut: boolean
  disconnectPrivyAndEOAs: () => Promise<void>
}

type SignerForMatching = { address: string; email?: string }

/**
 * Manages the Privy embedded wallet lifecycle:
 * - Auto-creates an embedded wallet after Privy login if none exists
 * - Auto-activates the embedded wallet for wagmi via @privy-io/wagmi
 * - Cleans up the Privy session (logout + reset state) on demand
 *
 * Pass `eoaSigners` when the caller knows which smart account we're signing
 * for. The backend derives a fresh Privy wallet_index per (email, org), so a
 * single Privy user can own multiple embedded wallets with distinct addresses.
 * Without `eoaSigners`, the bridge falls back to the user's first embedded
 * wallet — which is wrong whenever that email is a signer on multiple orgs.
 */
export const usePrivyWalletBridge = ({
  eoaSigners,
}: {
  eoaSigners?: SignerForMatching[]
} = {}): PrivyWalletBridge => {
  const [isActivatingWallet, setIsActivatingWallet] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const { wallets } = useWallets()
  const { authenticated, user, logout: privyLogout } = usePrivy()
  const { setActiveWallet } = useSetActiveWallet()
  const { createWallet } = useCreateWallet()
  const { address: wagmiAddress, isConnected } = useAccount()
  const { disconnect } = useDisconnect()
  const hasActivatedWalletRef = useRef(false)
  const hasCreatedWalletRef = useRef(false)
  const lastPrivyEmailRef = useRef<string | undefined>(undefined)
  const mountedRef = useRef(true)
  const prevAuthenticatedRef = useRef(authenticated)
  const currentPrivyEmail = user?.email?.address

  // Set to true on every mount so StrictMode's dev-only
  // mount → unmount → remount cycle restores it after the first cleanup flips
  // it to false. Without this, `mountedRef.current` stays false for the rest of
  // the component's life and `setIsLoggingOut(false)` is permanently skipped —
  // stranding `isLoggingOut: true` so `hasPrivyUser` (which keys on
  // `isLoggingOut && lastPrivyEmailRef`) never returns to false.
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const disconnectPrivyAndEOAs = useCallback(async () => {
    if (!authenticated && !user && !isConnected) return

    lastPrivyEmailRef.current = currentPrivyEmail ?? lastPrivyEmailRef.current
    setIsLoggingOut(true)
    hasCreatedWalletRef.current = false
    hasActivatedWalletRef.current = false

    await Promise.allSettled([
      authenticated ? privyLogout().catch(captureException) : Promise.resolve(),
      Promise.resolve(disconnect()),
    ])

    if (mountedRef.current) {
      setIsLoggingOut(false)
    }
  }, [
    authenticated,
    currentPrivyEmail,
    disconnect,
    isConnected,
    privyLogout,
    user,
  ])

  // Reset wallet-lifecycle refs whenever authenticated flips true -> false,
  // including external Privy logouts (session expiry, another tab, etc.)
  // so a subsequent login correctly re-creates/re-activates the embedded wallet.
  useEffect(() => {
    if (prevAuthenticatedRef.current && !authenticated) {
      hasCreatedWalletRef.current = false
      hasActivatedWalletRef.current = false
    }
    prevAuthenticatedRef.current = authenticated
  }, [authenticated])

  useEffect(() => {
    if (currentPrivyEmail) {
      lastPrivyEmailRef.current = currentPrivyEmail
      return
    }

    if (isLoggingOut) {
      if (!authenticated && !user && !isConnected) {
        lastPrivyEmailRef.current = undefined
      }
      return
    }

    if (!authenticated) {
      lastPrivyEmailRef.current = undefined
    }
  }, [authenticated, currentPrivyEmail, isConnected, isLoggingOut, user])

  const privyEmail =
    currentPrivyEmail ?? (isLoggingOut ? lastPrivyEmailRef.current : undefined)
  const hasPrivyUser =
    authenticated || !!user || (isLoggingOut && !!lastPrivyEmailRef.current)

  // The signer address this Privy login is expected to produce for the current
  // smart account — i.e. the row in `eoaSigners` whose email matches the Privy
  // user's email. When provided, we use this to disambiguate among multiple
  // Privy embedded wallets owned by the same user (one per org). Undefined
  // when the caller didn't pass signers, or when the Privy user's email
  // doesn't match any signer for this account.
  const expectedSignerAddress = useMemo(() => {
    if (!currentPrivyEmail || !eoaSigners?.length) return undefined
    return eoaSigners.find(
      (s) => s.email && emailsMatch(s.email, currentPrivyEmail),
    )?.address
  }, [currentPrivyEmail, eoaSigners])

  const findEmbeddedWallet = useCallback(() => {
    if (expectedSignerAddress) {
      return wallets.find(
        (w) =>
          w.walletClientType === 'privy' &&
          Address.equal(w.address, expectedSignerAddress),
      )
    }
    return wallets.find((w) => w.walletClientType === 'privy')
  }, [wallets, expectedSignerAddress])

  // Auto-create embedded wallet after Privy login if none exists
  useEffect(() => {
    if (!authenticated || hasCreatedWalletRef.current || wallets.length > 0)
      return
    hasCreatedWalletRef.current = true
    createWallet().catch(() => {
      hasCreatedWalletRef.current = false
    })
  }, [authenticated, wallets.length, createWallet])

  // Auto-activate the embedded wallet for wagmi via @privy-io/wagmi.
  // Skip during teardown: disconnectPrivyAndEOAs preemptively resets
  // hasActivatedWalletRef.current so a subsequent login re-activates cleanly, but
  // that reset is observable on this render tick — if the effect re-evaluates
  // before Privy has actually flipped `authenticated` to false, it would kick
  // off a fresh setActiveWallet → wagmi connect() → POST privy.systems 400.
  useEffect(() => {
    if (!authenticated || isLoggingOut || hasActivatedWalletRef.current) return

    const embeddedWallet = findEmbeddedWallet()
    if (!embeddedWallet) return

    hasActivatedWalletRef.current = true
    setIsActivatingWallet(true)

    setActiveWallet(embeddedWallet).catch((error) => {
      captureException(error)
      if (mountedRef.current) {
        hasActivatedWalletRef.current = false
        setIsActivatingWallet(false)
      }
    })
  }, [authenticated, findEmbeddedWallet, setActiveWallet, isLoggingOut])

  // Clear the activating flag only once wagmi's useAccount() actually reflects
  // the embedded wallet address. setActiveWallet's promise resolves when Privy
  // hands wagmi the connector, but wagmi's React state propagates through its
  // own store. Gating on the observed address prevents the race where a click
  // between promise resolution and React state propagation falls through the
  // useEOASigning branch in useApproveAndSubmitTransaction.
  useEffect(() => {
    if (!isActivatingWallet || !wagmiAddress) return

    const embeddedWallet = findEmbeddedWallet()
    if (!embeddedWallet) return

    if (Address.equal(wagmiAddress, embeddedWallet.address)) {
      setIsActivatingWallet(false)
    }
  }, [isActivatingWallet, wagmiAddress, findEmbeddedWallet])

  return {
    authenticated,
    hasPrivyUser,
    privyEmail,
    isActivatingWallet,
    isLoggingOut,
    disconnectPrivyAndEOAs,
  }
}
